import React from 'react';
import moment from "moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, faCalendarAlt, faPlane, faHotel, faCar, faTasks, faStickyNote, 
    faClock, faMapMarkerAlt, faStar, faArrowRight, faCheckCircle, faTimesCircle 
} from '@fortawesome/free-solid-svg-icons';
import './PdfPreviewModal.css';

const FlightPdfCard = ({ flight, title }) => {
    if (!flight) return null;

    const leg = flight.dcDisplayLegs[0];
    const departureTime = moment(leg.startSegment.departureDateTime).format("HH:mm");
    const arrivalTime = moment(leg.endSegment.arrivalDateTime).format("HH:mm");
    const departureDate = moment(leg.startSegment.departureDateTime).format("ddd, MMM D, YYYY");
    const formatDuration = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
    const airlineLogo = `/assets/images/airlinelogos/${leg.airlineData.airlineCode}.png`;

    return (
        <div className="pdf-info-card pdf-flight-card">
            <div className="pdf-flight-header">
                <img src={airlineLogo} alt={leg.airlineData.airline} className="pdf-airline-logo" />
                <div>
                    <div className="pdf-airline-name">{title}: {leg.airlineData.airline}</div>
                    <div className="pdf-flight-number">{leg.airlineData.airlineCode} {leg.airlineData.flightNumber}</div>
                </div>
            </div>
            <div className="pdf-flight-details">
                <div className="pdf-flight-endpoint">
                    <div className="pdf-time">{departureTime}</div>
                    <div className="pdf-airport">{leg.startSegment.airportCode}</div>
                    <div className="pdf-city">{leg.startSegment.city}</div>
                    <div className="pdf-date">{departureDate}</div>
                </div>
                <div className="pdf-flight-path">
                    <div className="pdf-duration"><FontAwesomeIcon icon={faClock} /> {formatDuration(leg.totalDuration)}</div>
                    <div className="pdf-line">
                        <FontAwesomeIcon icon={faPlane} className="pdf-plane-icon" />
                    </div>
                    <div className="pdf-stops">{leg.stops === 0 ? 'Non-Stop' : `${leg.stops} Stop(s)`}</div>
                </div>
                <div className="pdf-flight-endpoint">
                    <div className="pdf-time">{arrivalTime}</div>
                    <div className="pdf-airport">{leg.endSegment.airportCode}</div>
                    <div className="pdf-city">{leg.endSegment.city}</div>
                    <div className="pdf-date">{moment(leg.endSegment.arrivalDateTime).format("ddd, MMM D, YYYY")}</div>
                </div>
            </div>
        </div>
    );
};


// --- Main Preview Modal Component ---
const PdfPreviewModal = ({ isOpen, onClose, pdfData }) => {
  if (!isOpen || !pdfData) return null;

  const { template, data } = pdfData; 
  const { selectedOnwardFlight, selectedReturnFlight, days } = data;

  const allHotels = days.map(d => d.hotel[0]).filter(Boolean);
  const uniqueHotels = [...new Map(allHotels.map(item => [item['hotelId'], item])).values()];
  const allTransfers = days.map(d => d.transfer).filter(Boolean);

  const inclusions = [ "Return economy class airfare", "Accommodation in specified hotels", "Daily breakfast at the hotel", "All transfers and sightseeing by private vehicle", "All applicable taxes" ];
  const exclusions = [ "Visa fees", "Travel insurance", "Lunch and dinner unless specified", "Any personal expenses", "Tips and porter charges" ];

  return (
    <div className="pdf-preview-backdrop">
      <div className="pdf-preview-content">
        <div className="pdf-preview-header">
          <h5>Preview: {template} Template</h5>
          <div className="pdf-actions">
            <button className="btn btn-sm btn-primary">Download PDF</button>
            <button className="pdf-close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        <div className="pdf-preview-body">
          <div className="pdf-document">
            <div className="pdf-doc-header">
                <img src="/assets/images/logo-dark.png" alt="Zotrai Logo" className="pdf-logo" />
                <h1>Your Trip to {days[0].area}</h1>
            </div>

            <div className="pdf-details-grid">
                <div className="pdf-section">
                    <h2 className="pdf-section-title">Package Details</h2>
                    <div className="pdf-package-details">
                        <p><strong>Duration:</strong> {days.length} Days / {days.length - 1} Nights</p>
                        <p><strong>Start Date:</strong> {moment(days[0].date).format("MMMM Do, YYYY")}</p>
                        <p><strong>Destinations:</strong> {days.map(d => d.area).filter((v, i, a) => a.indexOf(v) === i).join(', ')}</p>
                    </div>
                </div>
                <div className="pdf-section">
                    <h2 className="pdf-section-title">Overview</h2>
                    <p className="pdf-overview-text">
                        Enjoy a wonderful trip starting in the vibrant city of {days[0].area} and exploring the stunning sights of {days[1].area}. This itinerary is packed with exciting activities, comfortable stays, and seamless transfers for an unforgettable experience.
                    </p>
                </div>
            </div>

            <div className="pdf-section">
                <h2 className="pdf-section-title">Daily Itinerary</h2>
                {days.map((day) => (
                <div key={day.dayNumber} className="pdf-day-item">
                    <div className="pdf-day-timeline">
                        <div className="pdf-day-dot"></div>
                        {day.dayNumber !== days.length && <div className="pdf-day-line"></div>}
                    </div>
                    <div className="pdf-day-content">
                        <h5>Day {day.dayNumber}: {moment(day.date).format("dddd, MMM D")} - {day.area}</h5>
                        <p className="pdf-summary">{day.summary}</p>
                        
                        <ul className="pdf-activities-list">
                            {day.activities.map((act, i) => <li key={i}>{act}</li>)}
                        </ul>
                        
                        {day.notes && <p className="pdf-notes"><FontAwesomeIcon icon={faStickyNote} /> {day.notes}</p>}
                    </div>
                </div>
                ))}
            </div>
            
            <div className="pdf-section">
                <h2 className="pdf-section-title">Flight Details</h2>
                <FlightPdfCard flight={selectedOnwardFlight} title="Departure Flight" />
                <FlightPdfCard flight={selectedReturnFlight} title="Return Flight" />
            </div>

            <div className="pdf-section">
                <h2 className="pdf-section-title">Hotel Bookings</h2>
                <table className="pdf-table">
                    <thead>
                        <tr><th>Hotel Name</th><th>City</th><th>Rating</th></tr>
                    </thead>
                    <tbody>
                        {uniqueHotels.map(hotel => (
                            <tr key={hotel.hotelId}>
                                <td>{hotel.hotelName}</td>
                                <td>{hotel.cityName}</td>
                                <td>{hotel.rating} <FontAwesomeIcon icon={faStar} className="pdf-star-icon" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pdf-section">
                <h2 className="pdf-section-title">Transfers</h2>
                 <table className="pdf-table">
                    <thead>
                        <tr><th>Date</th><th>From</th><th>To</th><th>Vehicle</th></tr>
                    </thead>
                    <tbody>
                        {allTransfers.map((transfer, index) => (
                            <tr key={index}>
                                <td>{moment(days.find(d => d.dayNumber === transfer.dayNumber)?.date).format("ddd, MMM D")}</td>
                                <td>{transfer.from}</td>
                                <td>{transfer.to}</td>
                                <td>{transfer.vehicleType}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            <div className="pdf-section">
                 <div className="pdf-inc-exc-grid">
                    <div>
                        <h2 className="pdf-section-title">Inclusions</h2>
                        <ul className="pdf-inc-exc-list">
                            {inclusions.map((item, i) => <li key={i}><FontAwesomeIcon icon={faCheckCircle} className="pdf-include-icon"/> {item}</li>)}
                        </ul>
                    </div>
                    <div>
                        <h2 className="pdf-section-title">Exclusions</h2>
                        <ul className="pdf-inc-exc-list">
                            {exclusions.map((item, i) => <li key={i}><FontAwesomeIcon icon={faTimesCircle} className="pdf-exclude-icon"/> {item}</li>)}
                        </ul>
                    </div>
                 </div>
            </div>

            <div className="pdf-doc-footer">
                <p>Thank you for choosing Zotrai for your travel plans!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;